import { Box, Text, Paper } from "@mantine/core";
import { Button, Badge } from "@xplatform/design-system";
import { IconCircleCheck } from "@tabler/icons-react";
import type { FlowConfigViewModel } from "@models/doctype";
import { useTranslation } from "@hooks/useTranslation";
import { translateFlowType } from "@utils/flowTypeTranslations";

interface FlowInfoPanelProps {
    flowConfig: FlowConfigViewModel;
    docTypeCode?: string;
    onActivate: () => void;
}

export const FlowInfoPanel = ({
    flowConfig,
    docTypeCode,
    onActivate,
}: FlowInfoPanelProps) => {
    const { t } = useTranslation();

    return (
        <Box className="flow-diagram__info-section">
            <Box className="flow-diagram__info-header">
                <Text fw={600} size="lg">Flow Information</Text>
                <Box className="flow-diagram__header-actions">
                    <Badge color={flowConfig.isActive ? "green" : "gray"} size="lg">
                        {flowConfig.isActive ? "Active" : "Inactive"}
                    </Badge>
                    {!flowConfig.isActive && (
                        <Button
                            color="green"
                            leftSection={<IconCircleCheck size={16} />}
                            onClick={onActivate}
                            size="xs"
                        >
                            Activate
                        </Button>
                    )}
                </Box>
            </Box>
            <Paper className="flow-diagram__detail-panel" p="lg">
                <Box className="flow-diagram__metadata-grid">
                    <Box className="flow-diagram__metadata-item">
                        <Text size="xs" c="dimmed" fw={600}>{t("admin.table.columns.type")}</Text>
                        <Text size="sm">{translateFlowType(flowConfig.flowType, t)}</Text>
                    </Box>
                    <Box className="flow-diagram__metadata-item">
                        <Text size="xs" c="dimmed" fw={600}>{t("admin.table.columns.version")}</Text>
                        <Text size="sm">{flowConfig.version}</Text>
                    </Box>
                    {flowConfig.createdAt && (
                        <Box className="flow-diagram__metadata-item">
                            <Text size="xs" c="dimmed" fw={600}>{t("admin.table.columns.created")}</Text>
                            <Text size="sm">{flowConfig.createdAt.toLocaleString()}</Text>
                        </Box>
                    )}
                    {flowConfig.updatedAt && (
                        <Box className="flow-diagram__metadata-item">
                            <Text size="xs" c="dimmed" fw={600}>{t("admin.table.columns.updated")}</Text>
                            <Text size="sm">{flowConfig.updatedAt.toLocaleString()}</Text>
                        </Box>
                    )}
                </Box>

                <Box className="flow-diagram__ids-grid">
                    <Box className="flow-diagram__id-item">
                        <Text size="xs" c="dimmed" fw={600}>Config ID</Text>
                        <Text size="sm">{flowConfig.id}</Text>
                    </Box>
                    <Box className="flow-diagram__id-item">
                        <Text size="xs" c="dimmed" fw={600}>Flow Base ID</Text>
                        <Text size="sm">{flowConfig.flowBaseId}</Text>
                    </Box>
                    <Box className="flow-diagram__id-item">
                        <Text size="xs" c="dimmed" fw={600}>Doc Type ID</Text>
                        <Text size="sm">{flowConfig.docTypeId}</Text>
                    </Box>
                    {docTypeCode && (
                        <Box className="flow-diagram__id-item">
                            <Text size="xs" c="dimmed" fw={600}>Doc Type Code</Text>
                            <Text size="sm">{docTypeCode}</Text>
                        </Box>
                    )}
                </Box>

                {flowConfig.flowDescription && (
                    <Box className="flow-diagram__description-section">
                        <Text size="xs" c="dimmed" fw={600}>{t("admin.table.columns.description")}</Text>
                        <Text size="sm">{flowConfig.flowDescription}</Text>
                    </Box>
                )}
            </Paper>
        </Box>
    );
};
